import React from 'react';
import Card from '../../../components/base/Card';

function JeansSalesTab({ salesData }) {
  const jeansData = salesData?.jeans || {
    total: 2450000,
    pieces: 980,
    growth: 12.5,
    orders: 980,
    avgOrder: 2500,
    dailyData: [
      { date: '2026-02-01', sales: 45000, orders: 18, pieces: 180 },
      { date: '2026-02-02', sales: 52000, orders: 21, pieces: 210 },
      { date: '2026-02-03', sales: 38000, orders: 15, pieces: 150 },
      { date: '2026-02-04', sales: 65000, orders: 26, pieces: 260 },
      { date: '2026-02-05', sales: 48000, orders: 19, pieces: 190 },
      { date: '2026-02-06', sales: 71000, orders: 28, pieces: 280 },
      { date: '2026-02-07', sales: 55000, orders: 22, pieces: 220 }
    ]
  };

  const jeansProducts = [
    { name: 'Classic Indigo Slim Jeans', color: 'Blue', sales: 845000, units: 35, pieces: 350, growth: 15.2 },
    { name: 'Dark Wash Regular Jeans', color: 'Dark Blue', sales: 720000, units: 30, pieces: 300, growth: 8.7 },
    { name: 'Black Washed Skinny Jeans', color: 'Jet Black', sales: 595000, units: 25, pieces: 200, growth: 12.1 },
    { name: 'Grey Distressed Casual Jeans', color: 'Light Grey', sales: 290000, units: 18, pieces: 180, growth: 5.4 }
  ];

  const maxSales = Math.max(...(jeansData.dailyData?.map(d => d.sales) || [100000]), 1);

  return (
    <div className="space-y-6">
      {/* 4 Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900">₹{jeansData.total?.toLocaleString()}</p>
              <p className="text-xs text-green-600 font-medium">+{jeansData.growth}% growth</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-indigo-100 rounded-full">
              <i className="ri-shirt-line text-2xl text-indigo-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Pieces Sold</p>
              <p className="text-2xl font-bold text-gray-900">{jeansData.pieces} Pcs</p>
              <p className="text-xs text-blue-600">Denim Lot Units</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <i className="ri-shopping-bag-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{jeansData.orders}</p>
              <p className="text-xs text-green-600">Jeans Category</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-full">
              <i className="ri-calculator-line text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
              <p className="text-xl font-bold text-gray-900">₹{jeansData.avgOrder?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Per Order Average</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Jeans Daily Trend Chart */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Jeans Sales Trend</h3>
            <p className="text-sm text-gray-500">Daily sales revenue and piece count progression</p>
          </div>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
            Jeans Category
          </span>
        </div>

        <div className="relative h-64 bg-gray-50 rounded-lg p-4">
          <svg width="100%" height="200" className="overflow-visible">
            {[0, 25, 50, 75, 100].map(y => (
              <line
                key={y}
                x1="40"
                y1={40 + (y * 1.2)}
                x2="95%"
                y2={40 + (y * 1.2)}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            ))}

            {/* Line graph */}
            <polyline
              fill="none"
              stroke="#3B82F6"
              strokeWidth="3"
              points={jeansData.dailyData
                ?.map((d, i) => {
                  const x = 50 + i * 110;
                  const y = 180 - ((d.sales / maxSales) * 140);
                  return `${x},${y}`;
                })
                .join(" ")}
            />

            {/* Points */}
            {jeansData.dailyData?.map((d, i) => {
              const x = 50 + i * 110;
              const y = 180 - ((d.sales / maxSales) * 140);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#2563EB"
                    className="hover:r-7 cursor-pointer transition-all"
                  >
                    <title>{`${d.date}: ₹${d.sales.toLocaleString()} | ${d.pieces} Pcs`}</title>
                  </circle>
                  <text x={x} y="195" textAnchor="middle" className="text-xs fill-gray-500">
                    {d.date.split("-").slice(1).join("/")}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </Card>

      {/* Top Jeans Products Table */}
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Jeans Variants</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pieces Sold</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {jeansProducts.map((p, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{p.color}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">₹{p.sales.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.pieces} Pcs ({p.units} Lots)</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      +{p.growth}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default JeansSalesTab;
